// 画像変換情報の管理
import { imageTransforms, updateImageTransform } from "./state.js";
import { validateAndNormalizeTransform, isTransformDataEqual } from "./utils.js";

// 変換情報を初期化する関数
export const initializeImageTransform = (imageIndex) => {
  // 入力値検証
  if (imageIndex < 0 || imageIndex >= 4) {
    console.error(`[initializeImageTransform] 無効な画像インデックス: ${imageIndex}`);
    return false;
  }

  // デフォルトの変換情報
  const defaultTransform = { scale: 1, rotation: 0, offsetX: 0, offsetY: 0 };

  // imageTransforms配列を更新
  updateImageTransform(imageIndex, defaultTransform);

  // hidden inputを更新
  const success = updateHiddenTransformInput(imageIndex, defaultTransform);

  if (success) {
    console.log(`[initializeImageTransform] 画像 ${imageIndex + 1} の変換情報を初期化:`, defaultTransform);
  }

  return success;
};

// hidden inputの変換情報を更新する関数
export const updateHiddenTransformInput = (imageIndex, transformData) => {
  // 入力値検証
  if (imageIndex < 0 || imageIndex >= 4) {
    console.error(`[updateHiddenTransformInput] 無効な画像インデックス: ${imageIndex}`);
    return false;
  }

  if (!transformData || typeof transformData !== "object") {
    console.error(`[updateHiddenTransformInput] 無効な変換データ:`, transformData);
    return false;
  }

  // 変換データの検証と正規化
  const validatedTransform = validateAndNormalizeTransform(transformData);
  if (!validatedTransform) {
    console.error(`[updateHiddenTransformInput] 変換データの検証に失敗:`, transformData);
    return false;
  }

  const transformInputId = `mase_case_entry_type_a1_itemFile${String(imageIndex + 1).padStart(2, "0")}_transform`;
  const transformInput = document.getElementById(transformInputId);

  if (transformInput) {
    try {
      const jsonString = JSON.stringify(validatedTransform);
      transformInput.value = jsonString;
      console.log(`[updateHiddenTransformInput] Hidden input ${transformInputId} を更新:`, jsonString);

      // データ整合性チェック
      const parsedBack = JSON.parse(jsonString);
      if (!isTransformDataEqual(validatedTransform, parsedBack)) {
        console.warn(`[updateHiddenTransformInput] データ整合性チェックに失敗`);
        return false;
      }

      return true;
    } catch (error) {
      console.error(`[updateHiddenTransformInput] JSON変換エラー:`, error);
      return false;
    }
  } else {
    console.warn(`[updateHiddenTransformInput] Hidden input ${transformInputId} が見つかりません`);
    return false;
  }
};

// 全ての変換情報をhidden inputに保存する関数
export const saveAllTransformsToHiddenInputs = () => {
  let successCount = 0;
  const errors = [];

  for (let i = 0; i < 4; i++) {
    const success = updateHiddenTransformInput(i, imageTransforms[i]);
    if (success) {
      successCount++;
    } else {
      errors.push(`画像 ${i + 1}`);
    }
  }

  if (errors.length > 0) {
    console.warn(`[saveAllTransformsToHiddenInputs] 一部の保存に失敗: ${errors.join(", ")}`);
  }

  console.log(`[saveAllTransformsToHiddenInputs] ${successCount}/4 の変換情報をhidden inputに保存しました`);
  return successCount === 4;
};

// hidden inputから変換情報を復元する関数
export const loadTransformFromHiddenInput = (imageIndex) => {
  if (imageIndex < 0 || imageIndex >= 4) {
    console.error(`[loadTransformFromHiddenInput] 無効な画像インデックス: ${imageIndex}`);
    return null;
  }

  const transformInputId = `mase_case_entry_type_a1_itemFile${String(imageIndex + 1).padStart(2, "0")}_transform`;
  const transformInput = document.getElementById(transformInputId);

  if (!transformInput) {
    console.warn(`[loadTransformFromHiddenInput] Hidden input ${transformInputId} が見つかりません`);
    return null;
  }

  try {
    const jsonString = transformInput.value;
    if (!jsonString) {
      console.log(`[loadTransformFromHiddenInput] 画像 ${imageIndex + 1} のhidden inputは空です`);
      return null;
    }

    const parsedTransform = JSON.parse(jsonString);
    const validatedTransform = validateAndNormalizeTransform(parsedTransform);

    if (validatedTransform) {
      console.log(`[loadTransformFromHiddenInput] 画像 ${imageIndex + 1} の変換情報を復元:`, validatedTransform);
      return validatedTransform;
    } else {
      console.warn(`[loadTransformFromHiddenInput] 画像 ${imageIndex + 1} の変換データが無効です:`, parsedTransform);
      return null;
    }
  } catch (error) {
    console.error(`[loadTransformFromHiddenInput] 画像 ${imageIndex + 1} のJSON解析エラー:`, error);
    return null;
  }
};

// 全ての変換情報をhidden inputから復元する関数
export const loadAllTransformsFromHiddenInputs = () => {
  let loadedCount = 0;

  for (let i = 0; i < 4; i++) {
    const loadedTransform = loadTransformFromHiddenInput(i);
    if (loadedTransform) {
      updateImageTransform(i, loadedTransform);
      loadedCount++;
    } else {
      // デフォルト値を設定
      updateImageTransform(i, { scale: 1, rotation: 0, offsetX: 0, offsetY: 0 });
      console.log(`[loadAllTransformsFromHiddenInputs] 画像 ${i + 1} にデフォルト値を設定`);
    }
  }

  console.log(`[loadAllTransformsFromHiddenInputs] ${loadedCount}/4 の変換情報を復元しました`);
  return loadedCount;
};

// デバッグ用：全ての変換情報とhidden inputの状態を表示
export const debugTransformState = () => {
  console.group("[debugTransformState] 変換情報の状態");

  for (let i = 0; i < 4; i++) {
    const transformInputId = `mase_case_entry_type_a1_itemFile${String(i + 1).padStart(2, "0")}_transform`;
    const transformInput = document.getElementById(transformInputId);

    console.log(`画像 ${i + 1}:`);
    console.log(`  imageTransforms[${i}]:`, JSON.stringify(imageTransforms[i]));
    console.log(`  hidden input value:`, transformInput ? transformInput.value : "NOT FOUND");

    if (transformInput && transformInput.value) {
      try {
        const parsed = JSON.parse(transformInput.value);
        const isEqual = isTransformDataEqual(imageTransforms[i], parsed);
        console.log(`  データ整合性:`, isEqual ? "✓" : "✗");
      } catch (error) {
        console.log(`  JSON解析エラー:`, error.message);
      }
    }
  }

  console.groupEnd();
};
